import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { Home, LogOut, Download, Users, LogIn, UserPlus, Activity, Loader2, ShieldCheck, Globe, Monitor, Smartphone } from "lucide-react";

const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "all", label: "All time" },
];
const EVENTS = [
  { key: "all", label: "All" },
  { key: "login", label: "Logins" },
  { key: "register", label: "Registrations" },
];
const VIEWS = [
  { key: "auth", label: "Logins & Signups" },
  { key: "visits", label: "Page Visits" },
];

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="gs-card p-4" data-testid={testId}>
      <div className="text-xs text-slate-400 font-semibold flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-mono font-bold text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function EventPill({ event }) {
  const isLogin = event === "login";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isLogin ? "bg-trust-light text-trust" : "bg-terracotta-light text-terracotta-dark"}`}>
      {isLogin ? <LogIn className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}{isLogin ? "Login" : "Register"}
    </span>
  );
}

function FunnelStep({ label, value }) {
  return (
    <div className="text-center" data-testid={`funnel-${label.toLowerCase()}`}>
      <div className="font-mono font-bold text-xl text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 font-semibold">{label}</div>
    </div>
  );
}

const fmt = (iso) => {  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("auth");
  const [range, setRange] = useState("7d");
  const [event, setEvent] = useState("all");
  const [data, setData] = useState({ events: [], summary: {} });
  const [series, setSeries] = useState([]);
  const [visits, setVisits] = useState({ visits: [], summary: {}, devices: {} });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [adminEmail, setAdminEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [funnel, setFunnel] = useState({});
  const [funnelTrend, setFunnelTrend] = useState([]);

  const loadRecipients = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/report-recipients");
      setRecipients(data.recipients || []);
      setAdminEmail(data.admin_email || "");
    } catch (e) { console.warn("recipients load failed", e); }
  }, []);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const addRecipient = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    try {
      const { data } = await api.post("/admin/report-recipients", { email: e });
      setRecipients(data.recipients || []);
      setNewEmail("");
    } catch (err) { alert("Please enter a valid email address."); }
  };

  const removeRecipient = async (e) => {
    try {
      const { data } = await api.delete(`/admin/report-recipients/${encodeURIComponent(e)}`);
      setRecipients(data.recipients || []);
    } catch (err) { console.warn("remove failed", err); }
  };

  const loadAuth = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, dl] = await Promise.all([
        api.get("/admin/access-events", { params: { range, event } }),
        api.get("/admin/access-events/daily", { params: { range: range === "7d" ? "7d" : "30d" } }),
      ]);
      setData(ev.data);
      setSeries(dl.data.series || []);
    } catch (e) { console.warn("admin auth load failed", e); }
    finally { setLoading(false); }
  }, [range, event]);

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const [v, f, t] = await Promise.all([
        api.get("/admin/visits", { params: { range } }),
        api.get("/admin/funnel", { params: { range } }),
        api.get("/admin/funnel/trend", { params: { weeks: 8 } }),
      ]);
      setVisits(v.data);
      setFunnel(f.data);
      setFunnelTrend(t.data.series || []);
    } catch (e) { console.warn("admin visits load failed", e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { if (view === "auth") loadAuth(); else loadVisits(); }, [view, loadAuth, loadVisits]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/access-events/export", { params: { range, event }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `access-log-${range}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not download the report. Please try again.");
    } finally { setDownloading(false); }
  };

  if (user && user.role !== "admin") return <Navigate to="/home" replace />;
  const s = data.summary || {};
  const vs = visits.summary || {};

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-terracotta flex items-center justify-center text-white"><Home className="w-5 h-5" strokeWidth={2.5} /></div>
        <div className="font-head font-extrabold text-lg text-slate-900">My Manger</div>
        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2 py-0.5 text-xs font-bold"><ShieldCheck className="w-3 h-3" /> Admin</span>
        <button onClick={logout} data-testid="admin-logout-btn" className="ml-auto flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-red-500">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-head text-2xl font-bold text-slate-900">Activity</h1>
            <p className="text-sm text-slate-500">Everyone who logged in, registered, or browsed the app.</p>
          </div>
          {view === "auth" && (
            <button onClick={downloadExcel} disabled={downloading} data-testid="admin-download-excel-btn"
              className="gs-tap flex items-center gap-2 rounded-xl bg-trust text-white font-semibold px-4 py-2.5 text-sm shadow-sm disabled:opacity-60">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Excel
            </button>
          )}
        </div>

        <div className="flex rounded-xl bg-white border border-slate-200 p-1 text-sm font-semibold w-fit" data-testid="admin-view-tabs">
          {VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`admin-view-${v.key}`}
              className={`px-4 py-1.5 rounded-lg ${view === v.key ? "bg-terracotta text-white" : "text-slate-500"}`}>{v.label}</button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex rounded-xl bg-slate-100 p-1 text-sm font-semibold" data-testid="admin-range-tabs">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRange(r.key)} data-testid={`admin-range-${r.key}`}
                className={`px-3 py-1.5 rounded-lg ${range === r.key ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{r.label}</button>
            ))}
          </div>
          {view === "auth" && (
            <div className="flex rounded-xl bg-slate-100 p-1 text-sm font-semibold" data-testid="admin-event-tabs">
              {EVENTS.map((e) => (
                <button key={e.key} onClick={() => setEvent(e.key)} data-testid={`admin-event-${e.key}`}
                  className={`px-3 py-1.5 rounded-lg ${event === e.key ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{e.label}</button>
              ))}
            </div>
          )}
        </div>

        {view === "auth" ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Activity} label="Total events" value={s.total || 0} testId="admin-stat-total" />
              <StatCard icon={LogIn} label="Logins" value={s.logins || 0} testId="admin-stat-logins" />
              <StatCard icon={UserPlus} label="Registrations" value={s.registrations || 0} testId="admin-stat-registrations" />
              <StatCard icon={Users} label="Unique visitors" value={s.unique_visitors || 0} testId="admin-stat-unique" />
            </div>

            <div className="gs-card p-4" data-testid="admin-daily-chart">
              <div className="text-sm font-semibold text-slate-700 mb-3">Daily trend</div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="logins" stroke="#0F9D8C" strokeWidth={2} dot={false} name="Logins" />
                    <Line type="monotone" dataKey="registrations" stroke="#D95338" strokeWidth={2} dot={false} name="Registrations" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="gs-card overflow-hidden">
              {loading ? (
                <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-terracotta" /></div>
              ) : (data.events || []).length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400" data-testid="admin-empty">No activity in this period yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-events-table">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold">Role</th>
                        <th className="px-4 py-3 font-semibold">Event</th>
                        <th className="px-4 py-3 font-semibold">When</th>
                        <th className="px-4 py-3 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.map((e, i) => (
                        <tr key={`${e.email}-${e.ts}-${i}`} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`admin-event-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-slate-800">{e.name || "—"}</td>
                          <td className="px-4 py-3 text-slate-600">{e.email}</td>
                          <td className="px-4 py-3 text-slate-500 capitalize">{e.role}</td>
                          <td className="px-4 py-3"><EventPill event={e.event} /></td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(e.ts)}</td>
                          <td className="px-4 py-3 text-slate-400 font-mono text-xs">{e.ip || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Globe} label="Page visits" value={vs.total || 0} testId="admin-visit-total" />
              <StatCard icon={Users} label="Unique IPs" value={vs.unique_ips || 0} testId="admin-visit-unique" />
              <StatCard icon={Monitor} label="Logged-in" value={vs.authenticated || 0} testId="admin-visit-auth" />
              <StatCard icon={Smartphone} label="Anonymous" value={vs.anonymous || 0} testId="admin-visit-anon" />
            </div>

            {Object.keys(visits.devices || {}).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="admin-device-breakdown">
                {Object.entries(visits.devices).map(([d, n]) => (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
                    {d}: {n}
                  </span>
                ))}
              </div>
            )}

            <div className="gs-card p-4" data-testid="admin-funnel">
              <div className="text-sm font-semibold text-slate-700 mb-3">Visitor → signup funnel</div>
              <div className="flex items-center gap-3 flex-wrap">
                <FunnelStep label="Visitors" value={funnel.visitors ?? 0} />
                <span className="text-slate-300">→</span>
                <FunnelStep label="Signups" value={funnel.registrations ?? 0} />
                <span className="text-slate-300">→</span>
                <FunnelStep label="Logins" value={funnel.logins ?? 0} />
                <div className="ml-auto text-right">
                  <div className="text-xs text-slate-400 font-semibold">Conversion</div>
                  <div className="font-mono font-bold text-xl text-trust" data-testid="funnel-conversion">{funnel.conversion_pct ?? 0}%</div>
                </div>
              </div>
              {funnelTrend.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100" data-testid="funnel-trend">
                  <div className="text-xs font-semibold text-slate-400 mb-2">Conversion rate — weekly</div>
                  <div style={{ width: "100%", height: 150 }}>
                    <ResponsiveContainer>
                      <LineChart data={funnelTrend} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                        <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                        <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" unit="%" />
                        <Tooltip />
                        <Line type="monotone" dataKey="conversion_pct" stroke="#0F9D8C" strokeWidth={2} dot={{ r: 2 }} name="Conversion %" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            <div className="gs-card overflow-hidden">
              {loading ? (
                <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-terracotta" /></div>
              ) : (visits.visits || []).length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400" data-testid="admin-visits-empty">No page visits recorded in this period yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-visits-table">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                        <th className="px-4 py-3 font-semibold">When</th>
                        <th className="px-4 py-3 font-semibold">Page</th>
                        <th className="px-4 py-3 font-semibold">Visitor</th>
                        <th className="px-4 py-3 font-semibold">Device</th>
                        <th className="px-4 py-3 font-semibold">Browser</th>
                        <th className="px-4 py-3 font-semibold">OS</th>
                        <th className="px-4 py-3 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.visits.map((v, i) => (
                        <tr key={`${v.ip}-${v.ts}-${i}`} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`admin-visit-row-${i}`}>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(v.ts)}</td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-xs">{v.path || "/"}</td>
                          <td className="px-4 py-3 text-slate-600">{v.authenticated ? (v.email || "user") : <span className="text-slate-400">anonymous</span>}</td>
                          <td className="px-4 py-3 text-slate-500">{v.device}</td>
                          <td className="px-4 py-3 text-slate-500">{v.browser}</td>
                          <td className="px-4 py-3 text-slate-500">{v.os}</td>
                          <td className="px-4 py-3 text-slate-400 font-mono text-xs">{v.ip || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        <div className="gs-card p-4" data-testid="report-recipients-card">
          <div className="text-sm font-semibold text-slate-700 mb-1">Weekly report recipients</div>
          <p className="text-xs text-slate-400 mb-3">These inboxes receive the Monday summary email. {adminEmail && <span className="font-medium">{adminEmail}</span>} always receives it.</p>
          <div className="flex gap-2 mb-3">
            <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addRecipient(); }} placeholder="name@example.com" data-testid="recipient-email-input"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/30" />
            <button onClick={addRecipient} data-testid="recipient-add-btn" className="gs-tap rounded-xl bg-terracotta text-white font-semibold px-4 text-sm">Add</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="recipient-list">
            {adminEmail && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">{adminEmail} (admin)</span>}
            {recipients.filter((r) => r !== adminEmail).map((r) => (
              <span key={r} data-testid={`recipient-${r}`} className="inline-flex items-center gap-1 rounded-full bg-trust-light px-3 py-1 text-xs font-semibold text-trust">
                {r}
                <button onClick={() => removeRecipient(r)} data-testid={`recipient-remove-${r}`} className="ml-1 text-trust/70 hover:text-red-500">✕</button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
